import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Box, Button, CircularProgress, Stack, Typography, useTheme } from '@mui/material';
import { BackButton } from '../../ses/components/BackButton';
import { PlayerShift } from '../types/PlayerShiftTypes';
import { formatGameClock } from '../utils/shiftUtils';
import { decodePeriodDisplay } from '@/shared/utils/getPeriodName';
import { useCurrentGame } from '../../games/context/CurrentGameContext';
import { Tracking } from '@/shared/services/tracking';
import { useGameTimeFormatter } from '../../games/hooks/useGameTimeFormatter';
import { formatInTimeZone } from 'date-fns-tz';
import { SportEvent } from '../../ses/types/SportEventTypes';
import { ClipEventMetadata } from '../../ses/components/ClipCreationContent';
import { MediaProvider, useMediaContext } from '../../ses/context/MediaContext';
import { ClipCreationForm } from '../../ses/components/ClipCreationForm';
import { ConfirmationDialogProvider } from '@/shared/components';
import useServerStateContext from '@/shared/hooks/useServerStateContext';
import SimpleBar from 'simplebar-react';
import 'simplebar-react/dist/simplebar.min.css';

const SHIFT_PADDING_MS = 5000;

interface ShiftClipViewProps {
  shift: PlayerShift;
  playerId: string;
  gameId: string;
  onClose: () => void;
  onCreateClip: (event: SportEvent, metadata: ClipEventMetadata) => void;
}

export const ShiftClipView: React.FC<ShiftClipViewProps> = (props) => (
  <MediaProvider>
    <ConfirmationDialogProvider>
      <ShiftClipViewInner {...props} />
    </ConfirmationDialogProvider>
  </MediaProvider>
);

const ShiftClipViewInner: React.FC<ShiftClipViewProps> = ({
  shift,
  onClose,
}) => {
  const theme = useTheme();
  const { gameTypeMetadata, homeTeamLogoPath, awayTeamLogoPath } = useCurrentGame();
  const { getActiveTimezone } = useGameTimeFormatter();
  const { availableCameras } = useServerStateContext();
  const {
    clipGroups,
    selectedEventId,
    isLoading: isClipGroupsLoading,
    setSelectedEvent,
    addDraftClipGroup,
    updateClipGroupTime,
    updateTrackedObject,
    createClipInGroup,
    deleteClipGroup,
  } = useMediaContext();

  const [syntheticEvent, setSyntheticEvent] = useState<SportEvent | null>(null);
  const hasInitialized = useRef(false);

  // Hide the form while MediaContext is still fetching persisted clip groups
  // after the synthetic event was set, to avoid an empty-state flicker.
  const isInitializing = !!syntheticEvent && isClipGroupsLoading;

  const periodDisplay = decodePeriodDisplay(shift.period, gameTypeMetadata?.periodNamingMapping?.[0]);
  const clockStart = formatGameClock(shift.gameClockStart);
  const clockEnd = formatGameClock(shift.gameClockEnd);
  const teamLogoPath = shift.team === 'home' ? homeTeamLogoPath : awayTeamLogoPath;

  const wallTimeSubtitle = useMemo(() => {
    const tz = getActiveTimezone();
    const start = formatInTimeZone(new Date(shift.startTimeMs), tz, 'h:mm:ss');
    const end = formatInTimeZone(new Date(shift.endTimeMs), tz, 'h:mm:ss a');
    return `${start} - ${end}`;
  }, [shift.startTimeMs, shift.endTimeMs, getActiveTimezone]);

  const addShiftDraft = useCallback(() => {
    addDraftClipGroup({
      name: `${shift.playerName} Shift #${shift.shiftNumber}`,
      trackedObjectId: shift.playerId,
      inTime: new Date(shift.startTimeMs - SHIFT_PADDING_MS),
      outTime: new Date(shift.endTimeMs + SHIFT_PADDING_MS),
      persistOnExportOnly: true,
    });
  }, [shift, addDraftClipGroup]);

  // On mount: select the shift's underlying SES event and add a local-only
  // draft clip group with the shift player pre-tracked. No backend write
  // happens here — the draft persists only when the user picks a camera
  // (createClipInGroup lazy-persists drafts marked persistOnExportOnly).
  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    const event = {
      id: shift.playbyplayId,
      payload: { type: 'shift', data: {} },
      clock: { period: shift.period, time: shift.gameClockStart },
    } as unknown as SportEvent;

    setSelectedEvent(event);
    setSyntheticEvent(event);
    addShiftDraft();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Not a board event — the board has no pin on this flow. Kept because it is
  // already shipped and additive. MediaContext still emits its own generic clip
  // event underneath this. Emitted only once createClipInGroup reports an
  // actual creation — its requeue and bail-out paths must not count.
  const handleCreateShiftClip = useCallback(
    async (...args: Parameters<typeof createClipInGroup>) => {
      const outcome = await createClipInGroup(...args);
      if (outcome === 'created') {
        Tracking.getInstance().track('Create Shift Clip', {
          category: 'Clips',
          playerId: shift.playerId,
          shiftId: shift.shiftId,
          shiftNumber: shift.shiftNumber,
        });
      }
      return outcome;
    },
    [createClipInGroup, shift],
  );

  const handleAddAnotherClip = useCallback(() => {
    if (!selectedEventId) return;
    addShiftDraft();
  }, [selectedEventId, addShiftDraft]);

  // Handle output mode (portrait/landscape) changes for shift clips
  const handleOutputModeChange = useCallback(
    async (groupId: string | number, outputMode: 'portrait' | 'landscape') => {
      if (!syntheticEvent) return;

      const playbyplayId = (syntheticEvent as any).id;

      try {
        const response = await fetch(
          `/v1.0/games/${(shift as any).gameId}/play-by-play/${playbyplayId}/clip-groups/${groupId}`,
          {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ outputMode }),
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to update output mode: ${response.statusText}`);
        }

        // Re-initialize the event to refresh clip groups after mode change
        setSelectedEvent(syntheticEvent);
      } catch (error) {
        console.error('Error changing output mode:', error);
        throw error;
      }
    },
    [syntheticEvent, shift, setSelectedEvent],
  );

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Back button */}
      <Box sx={{ px: 1.5, pt: 1.5, pb: 1.5 }}>
        <BackButton onClick={onClose} />
      </Box>

      {/* Header card */}
      <Box
        sx={{
          mx: '12px',
          mb: '12px',
          height: '66px',
          bgcolor: '#27272E',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          px: '14px',
          gap: '10px',
          flexShrink: 0,
        }}
      >
        {teamLogoPath && (
          <Box
            component="img"
            src={teamLogoPath}
            alt="team"
            sx={{ width: 27, height: 30, objectFit: 'contain', flexShrink: 0 }}
          />
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: 'Sohne, sans-serif',
              fontSize: '16px',
              fontWeight: 400,
              lineHeight: '22px',
              color: '#FFFFFF',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            Player Shift: {shift.playerName} {periodDisplay.abbreviated} {clockStart} - {clockEnd}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mt: '2px' }}>
            <Typography sx={{ fontSize: '14px', color: '#9797A5', lineHeight: '20px' }}>
              {periodDisplay.abbreviated} {clockStart}
            </Typography>
            <Box sx={{ width: '1px', height: '10px', bgcolor: '#4A4A55', flexShrink: 0 }} />
            <Typography sx={{ fontSize: '14px', color: '#9797A5', lineHeight: '20px' }}>
              {wallTimeSubtitle}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Scrollable clip groups area */}
      <SimpleBar style={{ flex: 1, minHeight: 0 }}>
        {isInitializing ? (
          <Stack direction="row" sx={{ py: 4, justifyContent: 'center', alignItems: 'center' }}>
            <CircularProgress size={20} sx={{ color: 'rgba(255,255,255,0.3)' }} />
          </Stack>
        ) : syntheticEvent ? (
          <>
            <ClipCreationForm
              clipGroups={clipGroups}
              event={syntheticEvent}
              availableCameras={availableCameras}
              onTimeChange={updateClipGroupTime}
              onCameraClick={handleCreateShiftClip}
              onDeleteClipGroup={deleteClipGroup}
              onTrackedObjectChange={updateTrackedObject as any}
              onOutputModeChange={handleOutputModeChange}
              isShiftClip={true}
            />
          </>
        ) : (
          <Stack direction="row" sx={{ py: 2, px: 1, justifyContent: 'center' }}>
            <Typography sx={{ color: theme.palette.cx.light[200], fontSize: '12px' }}>
              Failed to initialize. Try adding a new clip.
            </Typography>
          </Stack>
        )}
      </SimpleBar>

      {/* Sticky New Clip button when clip groups already exist */}
      {!isInitializing && clipGroups.length > 0 && (
        <Stack
          direction="row"
          sx={{
            justifyContent: 'center',
            py: 2,
            backgroundColor: theme.palette.cx.bg.surface,
            borderTop: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          <Button
            onClick={handleAddAnotherClip}
            disabled={!syntheticEvent}
            variant="outlined"
            color="inherit"
            sx={{
              py: 1.5,
              px: 8,
              textTransform: 'none',
              borderRadius: '8px',
              border: `1px dashed ${theme.palette.cx.border.default}`,
              backgroundColor: '#17171C',
              '&.Mui-disabled': {
                opacity: 0.5,
                color: 'rgba(255, 255, 255, 0.3)',
                border: `1px dashed ${theme.palette.cx.border.subtle}`,
              },
            }}
          >
            + New Clip
          </Button>
        </Stack>
      )}
    </Box>
  );
};
